import get from "lodash/get";
import * as React from "react";
import { connect } from "react-redux";

import {
  exchangeRedeem,
  getExchangesAction,
  getLedgerHead as getLedgerHeadAction,
  getRewardsAction,
  getShopifyVariants as getShopifyVariantsAction,
  getWidgetExchangesAction,
  rewardStoreExchangeRedeem,
  setExchangeId,
  setScreenName,
} from "../../actions";
import {
  RedemptionOptions as RedemptionOptionsComponent,
  RedemptionOptionsProps,
} from "../../components/RedemptionOptions/RedemptionOptions";
import { StoreState } from "../../reducers";
import {
  getExchanges,
  getIsLoadingExchanges,
} from "../../reducers/exchange/methods";
import {
  getCampaignId,
  getLedgerHead,
  getRewardRequestStatus,
  getWidgetConfig,
} from "../../reducers/methods";
import { getRewards, getShopifyVariants } from "../../reducers/reward/methods";
import { customerAuthenticationOccurred } from "../../utils/customer";
import {
  filterLoyaltyRedemptionOptions,
  filterRewardStoreRedemptionOptions,
} from "./filterRedemptionOptions";

interface RedemptionOptionsContainerProps extends RedemptionOptionsProps {
  campaignId?: string;
  currency?: string;
  exchangeRedeem: typeof exchangeRedeem;
  getExchangesAction: typeof getExchangesAction;
  getLedgerHeadAction: typeof getLedgerHeadAction;
  getShopifyVariantsAction: typeof getShopifyVariantsAction;
  getWidgetExchangesAction: typeof getWidgetExchangesAction;
  isLoadingRedeemExchanges: string;
  ledgerHead: ReturnType<typeof getLedgerHead>;
  redemptionOptions: ReturnType<typeof getExchanges>;
  requestStatus: ReturnType<typeof getRewardRequestStatus>;
  rewardStoreExchangeRedeem: typeof rewardStoreExchangeRedeem;
  rewardStoreWidgetId: string | undefined;
  shopifyVariants: ReturnType<typeof getShopifyVariants>;
  getRewardsAction: typeof getRewardsAction;
  rewards: ReturnType<typeof getRewards>;
}

class RedemptionOptions extends React.Component<RedemptionOptionsContainerProps> {
  /** Stable reference so RedemptionOptionsComponent does not re-render every time parent renders.
   * Passes originalEmail for both reward store and loyalty so original (non-normalized) email
   * is preserved on redemption when widgets only have normalized email. */
  redeem = (params: {
    exchangeId: string;
    originalEmail?: string;
  }) => {
    if (this.props.rewardStoreWidgetId) {
      this.props.rewardStoreExchangeRedeem({
        widgetId: this.props.rewardStoreWidgetId,
        exchangeId: params.exchangeId,
        originalEmail: params.originalEmail,
      });
    } else {
      this.props.exchangeRedeem(params);
    }
  };

  componentDidMount() {
    const isCustomerAuthenticated = get(
      this.props.data,
      "customer.authenticated",
      false
    );
    const hasConfig = get(this.props.config, "settings") != null;
    // Only fetch when we have config (so we know reward store vs loyalty path and correct currency).
    // If config has not arrived yet (e.g. merchant SDK sendConfigToWidget still in flight), skip mount
    // fetch; componentDidUpdate will run when config arrives and fetch with correct endpoints/currency.
    if (isCustomerAuthenticated && hasConfig) {
      this.fetchExchangesAndLedger();
    }
  }

  componentDidUpdate(prevProps: RedemptionOptionsContainerProps) {
    if (this.shouldFetchExchangesAndLedger(prevProps)) {
      this.fetchExchangesAndLedger();
    }
    this.fetchShopifyVariantsForFreeGiftsIfNeeded(prevProps);
  }

  render() {
    return (
      <RedemptionOptionsComponent
        {...this.props}
        redeem={this.redeem}
        isLoadingRedeemExchanges={this.props.isLoadingRedeemExchanges}
      />
    );
  }

  private shouldFetchExchangesAndLedger(
    prevProps: RedemptionOptionsContainerProps
  ): boolean {
    const authenticated = get(this.props.data, "customer.authenticated", false);
    const prevHasSettings = get(prevProps.config, "settings") != null;
    const hasSettings = get(this.props.config, "settings") != null;
    const prevRewardStoreWidgetId = get(
      prevProps.config,
      "settings.rewardStoreWidgetId"
    ) as string | undefined;
    const rewardStoreWidgetId = get(
      this.props.config,
      "settings.rewardStoreWidgetId"
    ) as string | undefined;
    // Single fetch gate: auth transition (only when config present so we use correct currency),
    // or config just arrived (reward store or loyalty). Prevents double fetch when auth + config arrive in same cycle.
    // rewardStoreWidgetId is only under settings, so hasSettings is implicit when it transitions; we guard explicitly for consistency.

    return (
      authenticated &&
      ((customerAuthenticationOccurred(prevProps.data, this.props.data) &&
        hasSettings) ||
        (hasSettings && rewardStoreWidgetId && !prevRewardStoreWidgetId) ||
        (hasSettings && !prevHasSettings))
    );
  }

  private fetchShopifyVariantsForFreeGiftsIfNeeded(
    prevProps: RedemptionOptionsContainerProps
  ): void {
    if (
      prevProps.redemptionOptions === this.props.redemptionOptions ||
      !this.props.redemptionOptions ||
      !this.props.redemptionOptions.length
    ) {
      return;
    }
    const variantIds: string[] = [];
    this.props.redemptionOptions.forEach((exchangeOption) => {
      if (
        exchangeOption.reward &&
        exchangeOption.reward.rewardType === "shopifyFreeGift" &&
        exchangeOption.status === "active"
      ) {
        const freeGiftConfig = exchangeOption.reward.shopifyFreeGiftConfig;
        if (
          freeGiftConfig &&
          freeGiftConfig.productVariant &&
          freeGiftConfig.productVariant.id
        ) {
          variantIds.push(freeGiftConfig.productVariant.id);
        }
        if (
          freeGiftConfig &&
          freeGiftConfig.fallbackProductVariant &&
          freeGiftConfig.fallbackProductVariant.id
        ) {
          variantIds.push(freeGiftConfig.fallbackProductVariant.id);
        }
      }
    });
    const uniqueVariantIds = variantIds.filter(
      (id, index, arr) => arr.indexOf(id) === index
    );
    if (uniqueVariantIds.length > 0) {
      this.props.getShopifyVariantsAction(uniqueVariantIds);
    }
  }

  private fetchExchangesAndLedger() {
    const rewardStoreWidgetId = get(
      this.props.config,
      "settings.rewardStoreWidgetId"
    ) as string | undefined;
    const currency: string =
      this.props.currency ||
      (get(this.props.config, "settings.ledgerCurrency") as
        | string
        | undefined) ||
      "loyalty";
    if (rewardStoreWidgetId) {
      this.props.getWidgetExchangesAction(rewardStoreWidgetId);
    } else {
      this.props.getExchangesAction({ campaignId: this.props.campaignId });
    }
    if (this.props.requestStatus.getLedger !== "REQUESTING") {
      this.props.getLedgerHeadAction({ currency });
    }
  }
}

function mapStateToProp(
  state: StoreState,
  props: RedemptionOptionsContainerProps
): Pick<
  RedemptionOptionsProps,
  | "isLoading"
  | "ledgerHead"
  | "redemptionOptions"
  | "requestStatus"
  | "shopifyVariants"
  | "rewards"
  | "currencyLabel"
  | "originalEmail"
> & {
  campaignId?: string;
  isLoadingRedeemExchanges: string;
  rewardStoreWidgetId: string | undefined;
} {
  const config = getWidgetConfig(state);
  const rewardStoreWidgetId = get(config, "settings.rewardStoreWidgetId") as
    | string
    | undefined;
  const ledgerCurrency = get(config, "settings.ledgerCurrency") as
    | string
    | undefined;
  const loyaltyCampaignId = getCampaignId(state);
  // In widget configs, `currency: "loyalty"` is a semantic marker (not a real
  // ledger currency label). Treat it as unknown so we can fall back to
  // settings.ledgerCurrency / ledgerHead.currency for matching.
  const normalizeCurrency = (value?: string): string | undefined => {
    if (!value) {
      return undefined;
    }
    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() === "loyalty") {
      return undefined;
    }

    return trimmed;
  };
  const loyaltyCurrency = (
    normalizeCurrency(props.currency) ||
    normalizeCurrency(ledgerCurrency) ||
    get(getLedgerHead(state), "currency")
  ) as string | undefined;
  const rawExchanges = getExchanges(state.exchange) || [];
  // Scope exchanges to the active widget. Reward store widgets filter by the
  // widget's configured ledger currency (campaign scoping is enforced server-side
  // via /reward-store-widgets/:widgetId/exchanges). Loyalty widgets filter by
  // the loyalty campaignId + loyalty currency, and drop any reward-store
  // exchanges that may have arrived in the response.
  const redemptionOptions =
    rewardStoreWidgetId && ledgerCurrency
      ? filterRewardStoreRedemptionOptions({
          exchanges: rawExchanges,
          widgetLedgerCurrency: ledgerCurrency,
        })
      : filterLoyaltyRedemptionOptions({
          exchanges: rawExchanges,
          loyaltyCampaignId,
          loyaltyCurrency,
        });

  return {
    campaignId: loyaltyCampaignId || undefined,
    isLoading: getIsLoadingExchanges(state.exchange),
    isLoadingRedeemExchanges: state.exchange.isLoadingRedeemExchanges || "",
    ledgerHead: getLedgerHead(state),
    redemptionOptions,
    requestStatus: getRewardRequestStatus(state),
    rewardStoreWidgetId,
    shopifyVariants: getShopifyVariants(state.reward),
    rewards: getRewards(state.reward),
    currencyLabel: get(config, "settings.currencyLabel") as string | undefined,
    originalEmail:
      get(props, "data.customer.originalEmail") || props.originalEmail,
  };
}

export default connect(mapStateToProp, {
  exchangeRedeem,
  getExchangesAction,
  getLedgerHeadAction,
  getWidgetExchangesAction,
  rewardStoreExchangeRedeem,
  setExchangeId,
  setScreenName,
  getShopifyVariantsAction,
  getRewardsAction,
})(RedemptionOptions);
